import { useEffect, useRef, useState } from "react";

export default function JarvisV3() {
  const [text, setText] = useState("I am JARVIS. Ready for commands.");
  const [voices, setVoices] = useState([]);
  const [speaking, setSpeaking] = useState(false);
  const [blink, setBlink] = useState(false);
  const [look, setLook] = useState({ x: 0, y: 0 });

  const containerRef = useRef(null);

  // Load voices
  useEffect(() => {
    const loadVoices = () => {
      setVoices(speechSynthesis.getVoices());
    };

    loadVoices();
    speechSynthesis.onvoiceschanged = loadVoices;
  }, []);

  // Eye tracking
  useEffect(() => {
    const handleMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 12;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;

      setLook({ x, y });
    };

    window.addEventListener("mousemove", handleMove);
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  // Blinking
  useEffect(() => {
    const interval = setInterval(() => {
      setBlink(true);
      setTimeout(() => setBlink(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(interval);
  }, []);

  // Speak function
  const speak = (voice) => {
    const utterance = new SpeechSynthesisUtterance(text);

    utterance.voice = voice;
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);

    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);

    setTimeout(() => setSpeaking(false), text.length * 100);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white relative overflow-hidden">

      {/* HUD background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(0,255,255,0.05),transparent)] animate-pulse" />
      <div className="absolute w-full h-full bg-[radial-gradient(circle_at_center,rgba(0,255,255,0.15),transparent_60%)]" />

      {/* CORE */}
      <div
        ref={containerRef}
        className="relative w-[60%] bg-white/5 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-6 shadow-[0_0_80px_rgba(0,255,255,0.15)]"
      >

        {/* AVATAR */}
        <div className="flex flex-col items-center relative">

          {/* glow */}
          <div
            className={`absolute w-48 h-48 rounded-full blur-3xl transition-all duration-300 ${
              speaking ? "bg-cyan-400/40" : "bg-cyan-500/10"
            }`}
          />

          {/* head */}
          <div
            className={`relative w-28 h-28 rounded-full border border-cyan-400/30 flex items-center justify-center transition-all duration-200 ${
              speaking ? "shadow-[0_0_70px_rgba(0,255,255,0.8)]" : ""
            }`}
            style={{
              transform: `translate(${look.x}px, ${look.y}px)`,
            }}
          >

            {/* eyes */}
            <div className="flex gap-4 absolute top-8">

              {/* left eye */}
              <div className="w-2 h-2 rounded-full bg-cyan-300 relative overflow-hidden">
                <div
                  className="w-1 h-1 bg-black rounded-full absolute"
                  style={{
                    transform: `translate(${look.x}px, ${look.y}px)`,
                  }}
                />
              </div>

              {/* right eye */}
              <div className="w-2 h-2 rounded-full bg-cyan-300 relative overflow-hidden">
                <div
                  className="w-1 h-1 bg-black rounded-full absolute"
                  style={{
                    transform: `translate(${look.x}px, ${look.y}px)`,
                  }}
                />
              </div>

            </div>

            {/* mouth */}
            <div
              className={`absolute bottom-7 w-10 rounded-full transition-all duration-150 ${
                speaking ? "h-4 bg-cyan-300 animate-pulse" : "h-1 bg-cyan-700"
              }`}
            />
          </div>

          {/* status */}
          <p className="text-cyan-300 mt-3 tracking-widest text-sm">
            {speaking ? "SPEAKING..." : blink ? "ANALYZING..." : "STANDBY MODE"}
          </p>
        </div>

        {/* INPUT */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full mt-5 p-3 bg-black/40 border border-cyan-500/30 rounded-lg outline-none focus:ring-2 focus:ring-cyan-400"
        />

        {/* VOICES */}
        <div className="mt-5 max-h-64 overflow-y-auto border border-cyan-500/10 rounded-lg">

          {voices.map((v) => (
            <div
              key={v.name}
              className="flex justify-between items-center p-3 border-b border-cyan-500/10 hover:bg-cyan-500/10 transition"
            >
              <div>
                <p className="text-sm font-semibold">{v.name}</p>
                <p className="text-xs text-cyan-300/70">{v.lang}</p>
              </div>

              <button
                onClick={() => speak(v)}
                className="text-cyan-300 hover:text-white text-xl"
              >
                ▶
              </button>
            </div>
          ))}
        </div>

        {/* WAVEFORM */}
        <div className="flex justify-center gap-1 mt-6 h-10 items-end">
          {[...Array(14)].map((_, i) => (
            <div
              key={i}
              className="w-1 bg-cyan-400 rounded-full transition-all"
              style={{
                height: speaking
                  ? `${10 + Math.sin(Date.now() / 180 + i) * 25}px`
                  : "6px",
                opacity: speaking ? 1 : 0.3,
              }}
            />
          ))}
        </div>

      </div>
    </div>
  );
}